import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  UserCog, Search, Edit, Wallet, User, Mail,
  Briefcase, IndianRupee, Save, Home, HandCoins, Plus, Trash2, AlertCircle, RefreshCw
} from "lucide-react";
import { useBusiness } from "@/contexts/BusinessContext";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from "@/components/ui/alert-dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StaffProfiles = () => {
  const { selectedBusinessId } = useBusiness();
  const [loading, setLoading] = useState(true);
  const [staff, setStaff] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [showSalaryDialog, setShowSalaryDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [userRole, setUserRole] = useState("");
  // Advance state
  const [showAdvanceDialog, setShowAdvanceDialog] = useState(false);
  const [advances, setAdvances] = useState([]);
  const [totalPending, setTotalPending] = useState(0);
  const [loadingAdvances, setLoadingAdvances] = useState(false);
  const [advanceForm, setAdvanceForm] = useState({ amount: "", date: new Date().toISOString().split("T")[0], payment_mode: "cash", remark: "" });
  const [savingAdvance, setSavingAdvance] = useState(false);
  const [deleteAdvanceId, setDeleteAdvanceId] = useState(null);
  const [pendingByUser, setPendingByUser] = useState({});
  
  const [salaryForm, setSalaryForm] = useState({
    basic_salary: 0,
    hra: 0,
    conveyance: 0,
    other_allowances: 0,
    pf_percentage: 0,
    esi_percentage: 0,
    tds_percentage: 0,
    overtime_rate_per_hour: 0,
    work_mode: "office"
  });

  useEffect(() => {
    fetchUserRole();
    fetchStaff();
    fetchPendingByUser();
  }, [selectedBusinessId]);

  const fetchPendingByUser = async () => {
    try {
      const url = selectedBusinessId
        ? `${BACKEND_URL}/api/staff-advances/pending-summary?business_id=${selectedBusinessId}`
        : `${BACKEND_URL}/api/staff-advances/pending-summary`;
      const res = await fetch(url, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setPendingByUser(data.pending_by_user || {});
      }
    } catch (e) { /* non-fatal */ }
  };

  const fetchAdvances = async (userId) => {
    setLoadingAdvances(true);
    try {
      const url = selectedBusinessId
        ? `${BACKEND_URL}/api/staff-advances?user_id=${userId}&business_id=${selectedBusinessId}`
        : `${BACKEND_URL}/api/staff-advances?user_id=${userId}`;
      const res = await fetch(url, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setAdvances(data.advances || []);
        setTotalPending(data.total_pending || 0);
      }
    } catch (e) { toast.error("Failed to load advances"); }
    finally { setLoadingAdvances(false); }
  };

  const openAdvanceDialog = (staffMember) => {
    setSelectedStaff(staffMember);
    setAdvanceForm({ amount: "", date: new Date().toISOString().split("T")[0], payment_mode: "cash", remark: "" });
    fetchAdvances(staffMember.user_id);
    setShowAdvanceDialog(true);
  };

  const handleAddAdvance = async () => {
    if (!selectedStaff) return;
    const amt = parseFloat(advanceForm.amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    setSavingAdvance(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/staff-advances`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          user_id: selectedStaff.user_id,
          amount: amt,
          date: advanceForm.date,
          payment_mode: advanceForm.payment_mode,
          remark: advanceForm.remark,
          business_id: selectedBusinessId || selectedStaff.business_id,
        }),
      });
      if (res.ok) {
        toast.success("Advance recorded");
        setAdvanceForm({ amount: "", date: new Date().toISOString().split("T")[0], payment_mode: "cash", remark: "" });
        fetchAdvances(selectedStaff.user_id);
        fetchPendingByUser();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to add advance");
      }
    } catch { toast.error("Network error"); }
    finally { setSavingAdvance(false); }
  };

  const handleDeleteAdvance = async () => {
    if (!deleteAdvanceId) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/staff-advances/${deleteAdvanceId}`, {
        method: "DELETE", credentials: "include",
      });
      if (res.ok) {
        toast.success("Advance deleted");
        fetchAdvances(selectedStaff.user_id);
        fetchPendingByUser();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to delete");
      }
    } catch { toast.error("Network error"); }
    finally { setDeleteAdvanceId(null); }
  };

  const fetchUserRole = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setUserRole(data.role);
      }
    } catch (error) {
      console.error("Error fetching user role:", error);
    }
  };

  const fetchStaff = async () => {
    try {
      setLoading(true);
      const url = selectedBusinessId
        ? `${BACKEND_URL}/api/attendance/staff?business_id=${selectedBusinessId}`
        : `${BACKEND_URL}/api/attendance/staff`;
      
      const response = await fetch(url, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setStaff(data);
      } else {
        toast.error("Failed to fetch staff");
      }
    } catch (error) {
      console.error("Error fetching staff:", error);
    } finally {
      setLoading(false);
    }
  };

  const canManageSalary = ["super_admin", "admin"].includes(userRole);

  const filteredStaff = staff.filter(s => {
    const query = searchQuery.toLowerCase();
    return (
      s.name?.toLowerCase().includes(query) ||
      s.email?.toLowerCase().includes(query) ||
      s.role?.toLowerCase().includes(query)
    );
  });

  const openSalaryDialog = (staffMember) => {
    setSelectedStaff(staffMember);
    const salary = staffMember.salary_structure || {};
    setSalaryForm({
      basic_salary: salary.basic_salary || 0,
      hra: salary.hra || 0,
      conveyance: salary.conveyance || 0,
      other_allowances: salary.other_allowances || 0,
      pf_percentage: salary.pf_percentage || 0,
      esi_percentage: salary.esi_percentage || 0,
      tds_percentage: salary.tds_percentage || 0,
      overtime_rate_per_hour: salary.overtime_rate_per_hour || 0,
      work_mode: salary.work_mode || "office"
    });
    setShowSalaryDialog(true);
  };

  const handleSaveSalary = async () => {
    if (!selectedStaff) return;

    setSaving(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/attendance/staff/${selectedStaff.user_id}/salary`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(salaryForm)
        }
      );

      if (response.ok) {
        toast.success("Salary structure updated successfully");
        setShowSalaryDialog(false);
        fetchStaff();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update salary");
      }
    } catch (error) {
      toast.error("Error updating salary structure");
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const calculateMonthlySalary = (salary) => {
    if (!salary) return 0;
    return (salary.basic_salary || 0) + 
           (salary.hra || 0) + 
           (salary.conveyance || 0) + 
           (salary.other_allowances || 0);
  };

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="staff-profiles-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
            <UserCog className="w-8 h-8 text-emerald-600" />
            Staff Profiles
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Manage staff profiles and salary structures
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => { fetchStaff(); fetchPendingByUser(); }}
          disabled={loading}
          data-testid="refresh-staff-profiles"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          type="text"
          placeholder="Search staff..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="staff-search-input"
        />
      </div>

      {/* Staff Table */}
      <Card className="border-slate-200 dark:border-slate-700">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
            </div>
          ) : filteredStaff.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-500">
              <UserCog className="w-12 h-12 mb-4 opacity-50" />
              <p>{searchQuery ? "No staff match your search" : "No staff found"}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 dark:bg-slate-800">
                    <TableHead className="font-semibold">#</TableHead>
                    <TableHead className="font-semibold">Name</TableHead>
                    <TableHead className="font-semibold">Email</TableHead>
                    <TableHead className="font-semibold">Role</TableHead>
                    <TableHead className="font-semibold text-center">WFH</TableHead>
                    <TableHead className="font-semibold">Monthly Salary</TableHead>
                    <TableHead className="font-semibold">Per Day</TableHead>
                    <TableHead className="font-semibold text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredStaff.map((s, index) => (
                    <TableRow 
                      key={s.user_id}
                      className="hover:bg-slate-50 dark:hover:bg-slate-800/50"
                      data-testid={`staff-row-${s.user_id}`}
                    >
                      <TableCell className="font-medium text-slate-500">
                        {index + 1}
                      </TableCell>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                            {s.picture ? (
                              <img src={s.picture} alt={s.name} className="w-8 h-8 rounded-full object-cover" />
                            ) : (
                              <User className="w-4 h-4 text-emerald-600" />
                            )}
                          </div>
                          {s.name || s.email?.split("@")[0]}
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-500">
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {s.email}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="px-2 py-1 text-xs bg-slate-100 dark:bg-slate-800 rounded-full capitalize">
                          {s.role?.replace("_", " ")}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        {canManageSalary ? (
                          <Switch
                            checked={s.allow_work_from_home === true}
                            onCheckedChange={async () => {
                              try {
                                const res = await fetch(`${BACKEND_URL}/api/attendance/staff/${s.user_id}/wfh`, {
                                  method: "PATCH",
                                  headers: { "Content-Type": "application/json" },
                                  credentials: "include",
                                });
                                if (res.ok) {
                                  const data = await res.json();
                                  setStaff(prev => prev.map(u => u.user_id === s.user_id ? { ...u, allow_work_from_home: data.allow_work_from_home } : u));
                                  toast.success(data.message || `WFH updated for ${s.name}`);
                                } else {
                                  toast.error("Failed to update WFH");
                                }
                              } catch { toast.error("Failed to update WFH"); }
                            }}
                          />
                        ) : (
                          <Badge variant={s.allow_work_from_home ? "default" : "secondary"} className={`text-xs ${s.allow_work_from_home ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-500"}`}>
                            {s.allow_work_from_home ? "Yes" : "No"}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="font-medium text-emerald-600">
                        <div>{formatCurrency(calculateMonthlySalary(s.salary_structure))}</div>
                        {pendingByUser[s.user_id] > 0 && (
                          <div className="flex items-center gap-1 text-[11px] text-red-600 mt-0.5">
                            <AlertCircle className="w-3 h-3" />
                            Advance: {formatCurrency(pendingByUser[s.user_id])} Pending
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-slate-500">
                        {formatCurrency(s.salary_structure?.per_day_salary || 0)}
                      </TableCell>
                      <TableCell className="text-right">
                        {canManageSalary && (
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openSalaryDialog(s)}
                              data-testid={`edit-salary-${s.user_id}`}
                            >
                              <Wallet className="w-4 h-4 mr-1" />
                              Salary
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openAdvanceDialog(s)}
                              className="text-amber-700 hover:text-amber-800 hover:bg-amber-50"
                              data-testid={`advance-${s.user_id}`}
                            >
                              <HandCoins className="w-4 h-4 mr-1" />
                              Advance
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Salary Structure Dialog */}
      <Dialog open={showSalaryDialog} onOpenChange={setShowSalaryDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="w-5 h-5 text-emerald-600" />
              Salary Structure - {selectedStaff?.name || selectedStaff?.email}
            </DialogTitle>
            <DialogDescription>
              Configure the salary components for this staff member
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            {/* Earnings */}
            <div className="space-y-3">
              <h4 className="font-medium text-sm text-slate-700 dark:text-slate-300 flex items-center gap-2">
                <IndianRupee className="w-4 h-4" />
                Earnings (Monthly)
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="basic_salary">Basic Salary</Label>
                  <Input
                    id="basic_salary"
                    type="number"
                    value={salaryForm.basic_salary}
                    onChange={(e) => setSalaryForm({...salaryForm, basic_salary: parseFloat(e.target.value) || 0})}
                    data-testid="basic-salary-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="hra">HRA</Label>
                  <Input
                    id="hra"
                    type="number"
                    value={salaryForm.hra}
                    onChange={(e) => setSalaryForm({...salaryForm, hra: parseFloat(e.target.value) || 0})}
                    data-testid="hra-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="conveyance">Conveyance</Label>
                  <Input
                    id="conveyance"
                    type="number"
                    value={salaryForm.conveyance}
                    onChange={(e) => setSalaryForm({...salaryForm, conveyance: parseFloat(e.target.value) || 0})}
                    data-testid="conveyance-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="other_allowances">Other Allowances</Label>
                  <Input
                    id="other_allowances"
                    type="number"
                    value={salaryForm.other_allowances}
                    onChange={(e) => setSalaryForm({...salaryForm, other_allowances: parseFloat(e.target.value) || 0})}
                    data-testid="other-allowances-input"
                  />
                </div>
              </div>
            </div>

            {/* Deductions */}
            <div className="space-y-3">
              <h4 className="font-medium text-sm text-slate-700 dark:text-slate-300">
                Deductions (%)
              </h4>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="pf_percentage">PF %</Label>
                  <Input
                    id="pf_percentage"
                    type="number"
                    step="0.1"
                    value={salaryForm.pf_percentage}
                    onChange={(e) => setSalaryForm({...salaryForm, pf_percentage: parseFloat(e.target.value) || 0})}
                    data-testid="pf-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="esi_percentage">ESI %</Label>
                  <Input
                    id="esi_percentage"
                    type="number"
                    step="0.1"
                    value={salaryForm.esi_percentage}
                    onChange={(e) => setSalaryForm({...salaryForm, esi_percentage: parseFloat(e.target.value) || 0})}
                    data-testid="esi-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tds_percentage">TDS %</Label>
                  <Input
                    id="tds_percentage"
                    type="number"
                    step="0.1"
                    value={salaryForm.tds_percentage}
                    onChange={(e) => setSalaryForm({...salaryForm, tds_percentage: parseFloat(e.target.value) || 0})}
                    data-testid="tds-input"
                  />
                </div>
              </div>
            </div>

            {/* Overtime */}
            <div className="space-y-3">
              <h4 className="font-medium text-sm text-slate-700 dark:text-slate-300">
                Overtime
              </h4>
              <div className="space-y-2">
                <Label htmlFor="work_mode">Work Mode</Label>
                <select
                  id="work_mode"
                  className="w-full h-10 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 text-sm"
                  value={salaryForm.work_mode || "office"}
                  onChange={(e) => setSalaryForm({...salaryForm, work_mode: e.target.value})}
                  data-testid="work-mode-select"
                >
                  <option value="office">Office</option>
                  <option value="wfh">Work From Home (no overtime)</option>
                  <option value="field">Field Duty</option>
                </select>
                {salaryForm.work_mode === "wfh" && (
                  <p className="text-xs text-yellow-700 dark:text-yellow-400">
                    Overtime is not applicable for employees working from home (WFH).
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="overtime_rate">Overtime Rate (per hour)</Label>
                <Input
                  id="overtime_rate"
                  type="number"
                  value={salaryForm.overtime_rate_per_hour}
                  onChange={(e) => setSalaryForm({...salaryForm, overtime_rate_per_hour: parseFloat(e.target.value) || 0})}
                  data-testid="overtime-rate-input"
                  disabled={salaryForm.work_mode === "wfh"}
                />
              </div>
            </div>

            {/* Summary */}
            <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="font-medium">Monthly Gross Salary</span>
                <span className="text-xl font-bold text-emerald-600">
                  {formatCurrency(
                    salaryForm.basic_salary + salaryForm.hra + 
                    salaryForm.conveyance + salaryForm.other_allowances
                  )}
                </span>
              </div>
              <div className="flex justify-between items-center mt-2 text-sm text-slate-600 dark:text-slate-400">
                <span>Per Day Salary</span>
                <span>
                  {formatCurrency(
                    (salaryForm.basic_salary + salaryForm.hra + 
                    salaryForm.conveyance + salaryForm.other_allowances) / 30
                  )}
                </span>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSalaryDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveSalary}
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="save-salary-btn"
            >
              {saving ? "Saving..." : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Advance Management Dialog */}
      <Dialog open={showAdvanceDialog} onOpenChange={setShowAdvanceDialog}>
        <DialogContent className="sm:max-w-[720px] max-h-[85vh] overflow-y-auto" data-testid="advance-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HandCoins className="w-5 h-5 text-amber-600" />
              Advance Management — {selectedStaff?.name || selectedStaff?.email}
            </DialogTitle>
            <DialogDescription>
              Record and adjust salary advances for this employee
            </DialogDescription>
          </DialogHeader>

          {/* Pending Summary */}
          <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg flex items-center justify-between">
            <div>
              <p className="text-xs text-amber-700 font-medium uppercase tracking-wide">Total Pending Advance</p>
              <p className="text-2xl font-bold text-amber-700">{formatCurrency(totalPending)}</p>
            </div>
            <AlertCircle className="w-8 h-8 text-amber-500" />
          </div>

          {/* Add Advance Form */}
          <div className="space-y-3 py-2">
            <h4 className="font-medium text-sm text-slate-700 dark:text-slate-300 flex items-center gap-2">
              <Plus className="w-4 h-4" />
              Add New Advance
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Amount (₹) *</Label>
                <Input
                  type="number"
                  value={advanceForm.amount}
                  onChange={(e) => setAdvanceForm({ ...advanceForm, amount: e.target.value })}
                  placeholder="e.g., 5000"
                  data-testid="advance-amount-input"
                />
              </div>
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={advanceForm.date}
                  onChange={(e) => setAdvanceForm({ ...advanceForm, date: e.target.value })}
                  max={new Date().toISOString().split("T")[0]}
                  data-testid="advance-date-input"
                />
              </div>
              <div>
                <Label>Payment Mode</Label>
                <Select value={advanceForm.payment_mode} onValueChange={(v) => setAdvanceForm({ ...advanceForm, payment_mode: v })}>
                  <SelectTrigger data-testid="advance-mode-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="bank">Bank Transfer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Remark (optional)</Label>
                <Input
                  value={advanceForm.remark}
                  onChange={(e) => setAdvanceForm({ ...advanceForm, remark: e.target.value })}
                  placeholder="Reason / notes"
                  data-testid="advance-remark-input"
                />
              </div>
            </div>
            <Button
              onClick={handleAddAdvance}
              disabled={savingAdvance || !advanceForm.amount}
              className="bg-amber-600 hover:bg-amber-700 text-white w-full md:w-auto"
              data-testid="add-advance-btn"
            >
              {savingAdvance ? "Adding..." : (<><Plus className="w-4 h-4 mr-1" /> Add Advance</>)}
            </Button>
          </div>

          {/* Advance History Table */}
          <div className="space-y-2 pt-2 border-t">
            <h4 className="font-medium text-sm text-slate-700 dark:text-slate-300">Advance History</h4>
            {loadingAdvances ? (
              <div className="text-center py-6 text-slate-500">Loading...</div>
            ) : advances.length === 0 ? (
              <div className="text-center py-6 text-slate-500 text-sm">No advances recorded yet</div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50 dark:bg-slate-800">
                      <TableHead className="text-xs">Date</TableHead>
                      <TableHead className="text-xs">Amount</TableHead>
                      <TableHead className="text-xs">Adjusted</TableHead>
                      <TableHead className="text-xs">Remaining</TableHead>
                      <TableHead className="text-xs">Mode</TableHead>
                      <TableHead className="text-xs">Status</TableHead>
                      <TableHead className="text-xs text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {advances.map((adv) => (
                      <TableRow key={adv.advance_id} data-testid={`advance-row-${adv.advance_id}`}>
                        <TableCell className="text-xs">{adv.date}</TableCell>
                        <TableCell className="text-xs font-medium">{formatCurrency(adv.amount)}</TableCell>
                        <TableCell className="text-xs text-slate-600">{formatCurrency(adv.adjusted_amount || 0)}</TableCell>
                        <TableCell className="text-xs font-bold text-amber-700">{formatCurrency(adv.remaining_amount || 0)}</TableCell>
                        <TableCell className="text-xs capitalize">{adv.payment_mode}</TableCell>
                        <TableCell>
                          <Badge className={adv.status === "adjusted" ? "bg-slate-200 text-slate-700 text-[10px]" : "bg-amber-100 text-amber-700 text-[10px]"}>
                            {adv.status === "adjusted" ? "Adjusted" : "Active"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => setDeleteAdvanceId(adv.advance_id)}
                            disabled={(adv.adjusted_amount || 0) > 0}
                            title={(adv.adjusted_amount || 0) > 0 ? "Cannot delete — partially/fully adjusted" : "Delete advance"}
                            data-testid={`delete-advance-${adv.advance_id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdvanceDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteAdvanceId} onOpenChange={() => setDeleteAdvanceId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Advance?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove this advance record. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteAdvance} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default StaffProfiles;
